import { Component, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { AccountService } from '../../models/account/accountService';
import { StorageService } from '../../core/storage/storageService';
import { CustomerService } from '../../models/customer/customerService';
import { MerchantService } from '../../models/merchant/merchantService';
import { GlobalSearchRecord } from '../../models/search/GlobalSearchRecord';
import { SearchFilters } from '../../models/search/searchFilters';
import { SearchService } from '../../models/search/searchService';
import { TransactionService } from '../../models/transactions/transactionService';
import { UserService } from '../../models/users/userService';
import { UIErrorHandler } from '../../core/uiErrorHandler';

@Component({
  selector: 'app-site-search',
  templateUrl: './site-search.component.html',
  standalone: false,
})
export class SiteSearchComponent {
  FILTER_TIMEOUT = 3000; // Time to wait before performing a filtered query
  // Increasing this will reduce server roundtrips,
  // but also create slower filter response.
  doFilterTimeout: boolean = false;
  queryInProgress: boolean = false;
  recordCount: number = 0;
  currentPage: number = 0;
  queryValueChanged: boolean = false;
  filterTimeoutId: number = 0;

  displayResults: boolean = false;
  displayFilterOptions: boolean = false;

  searchTerm: string = '';
  dataSource: GlobalSearchRecord[] = [];

  filterMerchants: boolean = true;
  filterCustomers: boolean = true;
  filterTransactions: boolean = true;
  filterBusinesses: boolean = true;
  filterUsers: boolean = true;

  mouseClickedInside: boolean = false;

  /// Listen for mouse clicks and hide the search results if
  /// the registered click was outside of this component.
  @HostListener('click')
  clickInside() {
    this.mouseClickedInside = true;
  }
  @HostListener('document:click')
  clickout() {
    if (!this.mouseClickedInside) {
      this.displayResults = false;
      this.displayFilterOptions = false;
    }
    this.mouseClickedInside = false;
  }

  constructor(
    private searchService: SearchService,
    private router: Router,
    private storageService: StorageService,
    private userService: UserService,
    private merchantService: MerchantService,
    private accountService: AccountService,
    private transactionService: TransactionService,
    private customerService: CustomerService,
    private uiErrorHandler: UIErrorHandler,
  ) {}

  ngAfterViewInit() {
    // This is necessary in order to set the width of the modal search results
    // to match the width of the text input area
    let componentContainer = document.getElementById('globalSearchContainer');
    let searchResultsContainer = document.getElementById(
      'searchResultsContainer',
    );
    let queryboxContainer = document.getElementById('searchBoxContainer');

    if (componentContainer?.offsetWidth) {
      searchResultsContainer?.style.setProperty(
        'width',
        componentContainer?.offsetWidth + 'px',
      );
    }

    if (queryboxContainer) {
      searchResultsContainer?.style.setProperty(
        'left',
        200 + queryboxContainer.offsetLeft + 'px',
      );
      searchResultsContainer?.style.setProperty(
        'top',
        queryboxContainer?.offsetTop +
          queryboxContainer?.offsetHeight +
          5 +
          'px',
      );
    }
  }

  txtSearch_keyup() {
    this.queryValueChanged = true;

    let query = this.searchTerm.trim();

    // TODO: bubble this check up to the service level at some point?
    // This keeps the querying "thread safe" by preventing
    // concurrent API calls, which could result in good data being
    // overwritten by stale data or other issues.
    if (!this.doFilterTimeout && !this.queryInProgress) {
      this.queryValueChanged = false;
      this._handleTheQuery(query);
    }

    let that = this;

    if (this.filterTimeoutId < 1) {
      // The idea here is that for fast typing, ideally we wouldn't fire off a
      // query for every character typed.  This would effectively lock out
      // querying for a set amount of time after the last query. It should be
      // considerably small, such as 500 millis.
      this.filterTimeoutId = window.setTimeout(() => {
        that.doFilterTimeout = false;

        if (this.queryValueChanged && !this.queryInProgress) {
          that._handleTheQuery(query);
        }
      }, this.FILTER_TIMEOUT);
    }
  }

  _handleTheQuery(query: string) {
    if (query.trim() == '') {
      return;
    }

    let that = this;

    that.queryInProgress = true;

    let filters = new SearchFilters({});
    filters.returnBusinesses = this.filterBusinesses;
    filters.returnCustomers = this.filterCustomers;
    filters.returnMerchants = this.filterMerchants;
    filters.returnTransactions = this.filterTransactions;
    filters.returnUsers = this.filterUsers;

    return this.searchService
      .doOmniSearch(query, filters, {
        pageNum: 0,
        pageSize: 20,
        orderBy: 'name',
        sortColumn: '',
        sortDirection: 'asc',
      })
      .then((results) => {
        this.dataSource = results.records;
        that.queryInProgress = false;

        that.displayResults = this.dataSource.length > 0;

        if (that.queryValueChanged) {
          that.queryValueChanged = false;
          that._handleTheQuery(that.searchTerm);
        }
      })
      .catch((message) => {
        this.uiErrorHandler.handleError(message);
      });
  }

  txtSearch_focus() {
    if (this.dataSource && this.dataSource.length > 0) {
      this.displayResults = true;
    }
  }

  txtSearch_blur() {
    //this.displayResults = false;
  }

  ddlFilters_click() {
    this.displayFilterOptions = !this.displayFilterOptions;

    if (
      this.displayFilterOptions &&
      this.dataSource &&
      this.dataSource.length > 0
    ) {
      this.displayResults = true;
    }
  }

  btnRefineFilters_click() {
    this.displayFilterOptions = false;
    this._handleTheQuery(this.searchTerm.trim());
  }

  selectRow_click(record: GlobalSearchRecord) {
    switch (record.type) {
      case 'transaction':
        this.routeToTransaction(record.id);
        break;
      case 'customer':
        this.routeToCustomer(record.id);
        break;
      case 'user':
        this.routeToUser(record.id);
        break;
      case 'merchant':
        this.routeToMerchant(record.id);
        break;
      case 'business':
        break;
    }

    this.displayFilterOptions = false;
    this.displayResults = false;
  }

  routeToTransaction(id: string) {
    this.transactionService.getTransaction(id).then((t) => {
      this.storageService.setSelectedTransaction(t);
      this.router.navigateByUrl('reroute?route=transaction');
    });
  }

  routeToUser(id: string) {
    this.userService.getUserById(id).then((u) => {
      this.storageService.setSelectedUser(u);
      this.router.navigateByUrl('reroute?route=users');
    });
  }

  routeToMerchant(id: string) {
    this.merchantService
      .getMerchantById(id)
      .then((merch) => {
        this.storageService.setSelectedLocation(merch);
        this.accountService
          .getAccount(merch.accountId)
          .then((account) => {
            this.storageService.setSelectedAccount(account);
            this.router.navigateByUrl('reroute?route=merchant/account');
          })
          .catch((message) => {
            this.uiErrorHandler.handleError(message);
          });
      })
      .catch((message) => {
        this.uiErrorHandler.handleError(message);
      });
  }

  routeToCustomer(id: string) {
    this.customerService.getCustomerById(id).then((c) => {
      this.storageService.setSelectedCustomer(c);
      this.router.navigateByUrl('reroute?route=customer-account');
    });
  }

  handleFiltersChanged() {
    this._handleTheQuery(this.searchTerm.trim());
  }

  formatDetails(type: string, details: any) {
    let html = details;

    if (type != '' && details != null) {
      let oDetails = JSON.parse(details);

      switch (type) {
        case 'business':
          html = oDetails.emailAddress;
          break;

        case 'customer':
          html = oDetails.emailAddress;
          break;

        case 'merchant':
          html = 'MID: ' + oDetails.mid + ', Location: ' + oDetails.street1;
          break;

        case 'transaction':
          html =
            oDetails.firstName +
            ' ' +
            oDetails.lastName +
            ' | ' +
            oDetails.cardType +
            ' ' +
            oDetails.cardNum +
            ' $' +
            oDetails.amount;
          break;

        case 'user':
          html = oDetails.emailAddress;
          break;

        default:
          break;
      }
    }

    return html;
  }

  getTypeTitle(type: string): string {
    switch (type) {
      case 'business':
        return 'Business';
        break;

      case 'customer':
        return 'Customer';
        break;

      case 'merchant':
        return 'Merchant';
        break;

      case 'transaction':
        return 'Transaction';
        break;

      case 'user':
        return 'User';
        break;

      default:
        return '';
    }
  }

  getTypeIcon(type: string): string {
    switch (type) {
      case 'business':
        return 'location_city'; //domain grid_on filter_none apps more layers
        break;

      case 'customer':
        return 'group'; //wc people people_outline
        break;

      case 'merchant':
        return 'store'; //store_mall_directory
        break;

      case 'transaction':
        return 'shopping_cart'; //receipt local_grocery_store credit_card attach_money
        break;

      case 'user':
        return 'person'; //person_outline account_circle account_box
        break;

      default:
        return '';
    }
  }
}
